import * as Sentry from '@sentry/react';

function detectEnvironment(): string {
  const hostname = window.location.hostname;
  if (hostname.includes('woolaby.art')) return 'production';
  if (hostname.includes('woolaby.dev') || hostname.includes('vercel.app'))
    return 'staging';
  return 'development';
}

// Matches torn-down Railway PR preview backend hostnames, which throw
// `TypeError: Failed to fetch` from stale Vercel preview tabs. See issue #459.
const PR_PREVIEW_BACKEND_PATTERN =
  /woolabyserver-woolaby-monorepo-pr-\d+\.up\.railway\.app/i;

function breadcrumbReferencesPrPreview(breadcrumb: Sentry.Breadcrumb): boolean {
  if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'xhr') {
    return false;
  }
  const url = (breadcrumb.data as { url?: unknown } | undefined)?.url;
  return typeof url === 'string' && PR_PREVIEW_BACKEND_PATTERN.test(url);
}

function isNetworkFailureEvent(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values) return false;
  return values.some(
    (ex) =>
      typeof ex.value === 'string' &&
      /Failed to fetch|NetworkError|Load failed/i.test(ex.value)
  );
}

Sentry.init({
  dsn: 'https://6d4df6385bed3779be08aed5e4324c66@o4510937279299584.ingest.us.sentry.io/4510937291489280',
  environment: detectEnvironment(),
  release: import.meta.env.VITE_SENTRY_RELEASE
    ? `woolaby-admin@${import.meta.env.VITE_SENTRY_RELEASE}`
    : undefined,
  enabled: !['localhost', '127.0.0.1'].includes(window.location.hostname),
  ignoreErrors: [
    // Dynamic-import and module-preload failures across Chrome/Firefox/Safari.
    // Mirrors client-fluffy's CHUNK_LOAD_ERROR_PATTERNS — admin has lazy chunks
    // but no shared handler module, so the list is duplicated inline.
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Error loading dynamically imported module/i,
    /Expected a JavaScript module script but the server responded with a MIME type of/i,
    /is not a valid JavaScript MIME type/i,
    /Unable to preload CSS/i,
    /Failed to fetch.*woolabyserver-woolaby-monorepo-pr-\d+\.up\.railway\.app/i,
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.2,
  beforeSend(event: Sentry.ErrorEvent) {
    if (!isNetworkFailureEvent(event)) return event;
    const requestUrl = event.request?.url;
    if (requestUrl && PR_PREVIEW_BACKEND_PATTERN.test(requestUrl)) {
      return null;
    }
    if (event.breadcrumbs?.some(breadcrumbReferencesPrPreview)) {
      return null;
    }
    return event;
  },
});
